import { renderTemplate } from "../templateRenderer";
import { REPAIR_TEMPLATE, REPAIR_CSS } from "./loader";
import { RepairJob } from "../../../Types/RepairJobTypes";

export interface RepairTemplateContext {
  job: RepairJob;
  config: {
    store_name: string;
    address: string;
    phone: string;
    currency?: string;
    receipt_footer?: string;
    [key: string]: any;
  };
  pageSize?: "80mm" | "A4" | "A3";
  isForPrint?: boolean;
}

const REPAIR_PAGE_SIZES = {
  A4: { width: "210mm", pad: "18mm", font: "13px" },
  A3: { width: "297mm", pad: "24mm", font: "15px" },
  "80mm": { width: "72mm", pad: "4mm", font: "11.5px" },
} as const;

const BASE_TEMPLATE = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  <meta name="viewport" content="width=device-width,initial-scale=1"/>
  <title>{{ title }}</title>
  <style>{{ styles }}</style>
</head>
<body>
  {{ content | safe }}
</body>
</html>`;

function buildCssVariables(ctx: RepairTemplateContext): string {
  const { pageSize = "80mm", isForPrint = false } = ctx;
  const is80 = pageSize === "80mm";
  const sz = REPAIR_PAGE_SIZES[pageSize];

  return `
    :root {
      --inv-font:       ${sz.font};
      --inv-width:      ${sz.width};
      --inv-pad:        ${isForPrint ? (is80 ? "0" : "12mm") : sz.pad};
      --inv-bg:         ${isForPrint ? "#fff" : "#e5e7eb"};
      --inv-body-pad:   ${isForPrint ? "0" : is80 ? "8px 4px" : "24px 16px"};
      --inv-shadow:     ${!isForPrint ? "0 4px 16px rgba(0,0,0,0.10)" : "none"};
      --inv-radius:     ${!isForPrint ? "4px" : "0"};
      --inv-th-pad:     ${is80 ? "4px 2px" : "7px 6px"};
      --inv-td-pad:     ${is80 ? "4px 2px" : "7px 6px"};
      --inv-h2-size:    ${is80 ? "1.15em" : "1.3em"};
      --inv-p-size:     ${is80 ? "0.82em" : "0.9em"};
      --inv-grand-size: ${is80 ? "1em" : "1.1em"};
      --inv-ftr-mt:     ${is80 ? "12px" : "24px"};
      --inv-ftr-size:   ${is80 ? "0.78em" : "0.82em"};
      --inv-page-size:  ${is80 ? "80mm auto" : pageSize};
    }
  `;
}

export function renderRepair(ctx: RepairTemplateContext): string {
  const { job, config, pageSize = "80mm" } = ctx;

  const currency = config.currency || "LKR";
  const customerName = job.customer_name || job.temp_customer_name || "Walk-in Customer";
  const customerPhone = job.customer_phone || job.temp_customer_phone || "";
  const deviceLabel = [job.device_brand, job.device_model].filter(Boolean).join(" ") || job.device_type;
  const dateObj = new Date(job.received_date || job.created_at);

  const contentContext = {
    storeName: config.store_name || "POS System",
    address: config.address || "",
    phone: config.phone || "",
    footer: config.receipt_footer || "Thank you for choosing us!",
    customerName,
    customerPhone,
    jobNo: job.job_number,
    dateStr: dateObj.toLocaleString(),
    deviceLabel,
    imei: job.device_imei || "",
    serialNumber: job.device_serial_number || "",
    devicePassword: job.device_password || "",
    diagnosis: job.diagnosis || "",
    technicianNotes: job.technician_notes || "",
    promisedDate: job.promised_date ? new Date(job.promised_date).toLocaleDateString() : "",
    reportedIssues: job.reported_issues,
    sellingPrice: job.selling_price,
    advancePayment: job.advance_payment,
    paidAmount: job.paid_amount,
    dueAmount: job.due_amount,
    currency,
    items: [],
    payments: job.payments || [],
    isA4: pageSize === "A4" || pageSize === "A3",
    logoUrl: config.store_logo_url || config.logo_url || "",
    customerAddress: job.customer_address || job.temp_customer_address || "",
    customerEmail: job.customer_email || job.temp_customer_email || "",
  };

  const content = renderTemplate(REPAIR_TEMPLATE, contentContext);
  const styles = buildCssVariables(ctx) + "\n" + REPAIR_CSS;

  return renderTemplate(BASE_TEMPLATE, {
    title: `Repair Ticket - ${job.job_number}`,
    styles,
    content,
  });
}
